import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewContainerRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModeleOrdonnancePanelService } from 'src/app/services/modele-ordonnance-panel/modele-ordonnance-panel.service';
import { CabinetService } from 'src/app/services/office/cabinet.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { ModeleOrdonnanceResume } from 'src/app/utils/interfaces/new/ModeleOrdonnanceResume';
import { ModeleOrdonnanceProfilResume } from 'src/app/utils/interfaces/new/ModeleOrdonnanceProfilResume';

@Component({
  selector: 'frg-modele-ordonnance-panel',
  templateUrl: './modele-ordonnance-panel.fragment.html',
  styleUrls: ['./modele-ordonnance-panel.fragment.scss']
})
export class ModeleOrdonnancePanelFragment implements OnInit, OnDestroy {

  @Output() useModele = new EventEmitter<string>();

  public collapsed = true;
  public isLoading = false;
  public currentTab: 'perso' | 'cabinet' = 'perso';

  public modelesPerso: ModeleOrdonnanceResume[] = [];
  public profilsCabinet: ModeleOrdonnanceProfilResume[] = [];
  public modelesCabinet = new Map<string, ModeleOrdonnanceResume[]>();
  public profilsExpanded = new Set<string>();

  private profilLabels = new Map<string, string>();
  private modelesChangedSub: Subscription;

  public searchPersoInput = '';
  public searchPerso = '';
  public profilsLoading = new Set<string>();

  public readonly cabinetPerPage = 30;
  public pagesCabinet = new Map<string, number>();
  public totalsCabinet = new Map<string, number>();
  public profilsLoadingMore = new Set<string>();

  public readonly persoPerPage = 30;
  public persoCurrentPage = 1;
  public persoTotal = 0;
  public isLoadingMorePerso = false;

  constructor(
    private _modeleOrdonnancePanel: ModeleOrdonnancePanelService,
    private _cabinet: CabinetService,
    private _modal: ModalService,
    private _view: ViewContainerRef
  ) {}

  public ngOnInit(): void {
    this.modelesChangedSub = this._modeleOrdonnancePanel.modelesChanged$.subscribe(async () => {
      if (!this.collapsed && this.currentTab === 'perso') {
        await this.loadModelesPerso();
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.modelesChangedSub) {
      this.modelesChangedSub.unsubscribe();
    }
  }

  public async togglePanel(): Promise<void> {
    this.collapsed = !this.collapsed;

    if (!this.collapsed) {
      if (this.currentTab === 'perso') {
        await this.loadModelesPerso();
      } else {
        await this.loadProfilsCabinet();
      }
    }
  }

  public async selectTab(tab: 'perso' | 'cabinet'): Promise<void> {
    this.currentTab = tab;

    if (tab === 'perso') {
      await this.loadModelesPerso();
    } else {
      await this.loadProfilsCabinet();
    }
  }

  public selectModele(idModele: string): void {
    if (!idModele) return;
    this.useModele.emit(idModele);
  }

  public async loadModelesPerso(): Promise<void> {
    this.isLoading = true;

    try {
      const result = await this._modeleOrdonnancePanel.find( 1, this.persoPerPage, this.searchPerso || undefined );

      if (result) {
        this.modelesPerso = result.objects || [];
        this.persoCurrentPage = 1;
        this.persoTotal = result.total;
      }
    } finally {
      this.isLoading = false;
    }
  }

  public async loadProfilsCabinet(): Promise<void> {
    this.isLoading = true;

    try {
      this.profilsCabinet = await this._modeleOrdonnancePanel.findProfilsCabinet() || [];

      if (this.profilLabels.size === 0) {
        await this.loadProfilLabels();
      }
    } finally {
      this.isLoading = false;
    }
  }

  public async toggleProfil(idProfil: string): Promise<void> {
    if (this.profilsExpanded.has(idProfil)) {
      this.profilsExpanded.delete(idProfil);
      return;
    }

    this.profilsExpanded.add(idProfil);

    if (!this.modelesCabinet.has(idProfil)) {
      this.profilsLoading.add(idProfil);

      try {
        const result = await this._modeleOrdonnancePanel.findByProfil(idProfil, 1, this.cabinetPerPage);

        if (result) {
          this.modelesCabinet.set(idProfil, result.objects || []);
          this.pagesCabinet.set(idProfil, 1);
          this.totalsCabinet.set(idProfil, result.total);

          const profil = this.profilsCabinet.find(p => p.idProfil === idProfil);
          if (profil) profil.total = result.total;
        }
      } finally {
        this.profilsLoading.delete(idProfil);
      }
    }
  }

  public getModelesCabinet(idProfil: string): ModeleOrdonnanceResume[] {
    return this.modelesCabinet.get(idProfil) || [];
  }

  public getProfilLabel(idProfil: string): string {
    return this.profilLabels.get(idProfil) || 'Praticien';
  }

  public async refreshProfilCabinet(idProfil: string, event?: MouseEvent): Promise<void> {
    if (event) event.stopPropagation();
    if (this.profilsLoading.has(idProfil)) return;

    this.profilsLoading.add(idProfil);

    try {
      this._modeleOrdonnancePanel.clearProfilCabinet(idProfil);

      const result = await this._modeleOrdonnancePanel.findByProfil( idProfil, 1, this.cabinetPerPage);

      if (result) {
        this.modelesCabinet.set(idProfil, result.objects || []);
        this.pagesCabinet.set(idProfil, 1);
        this.totalsCabinet.set(idProfil, result.total);

        const profil = this.profilsCabinet.find(p => p.idProfil === idProfil);
        if (profil) profil.total = result.total;
      }
    } finally {
      this.profilsLoading.delete(idProfil);
    }
  }

  public deleteModele(idModele: string, nom: string, event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }

    const confirm = this._modal.openModalConfirmation(
      this._view,
      'Suppression du modèle',
      `Voulez-vous supprimer le modèle "${nom}" ?`,
      []
    );

    const sub = confirm.instance.closeEmitter.subscribe(async (result: boolean) => {
      sub.unsubscribe();

      if (!result) {
        return;
      }

      await this._modeleOrdonnancePanel.delete(idModele);
    });
  }

  private async loadProfilLabels(): Promise<void> {
    const destinataires = await this._cabinet.findCurrentCabinetDestinataire();

    this.profilLabels.clear();

    const profilsActifs = (destinataires || []).filter(
      destinataire => !destinataire.disabled
    );

    const idsProfilsActifs = new Set(
      profilsActifs.map(destinataire => destinataire.id)
    );

    this.profilsCabinet = this.profilsCabinet.filter(
      profil => idsProfilsActifs.has(profil.idProfil)
    );

    for (const destinataire of profilsActifs) {
      const label = [
        destinataire.nom ? destinataire.nom.toLocaleUpperCase() : '',
        destinataire.prenom || ''
      ].filter(Boolean).join(' ');

      this.profilLabels.set(destinataire.id, label || 'Praticien');
    }
  }

  public onSearchPersoInput(event: Event): void {
    this.searchPersoInput = (event.target as HTMLInputElement).value;
  }

  public async searchModelesPerso(): Promise<void> {
    this.searchPerso = this.searchPersoInput.trim();
    await this.loadModelesPerso();
  }

  public async clearSearchPerso(): Promise<void> {
    this.searchPersoInput = '';
    this.searchPerso = '';
    await this.loadModelesPerso();
  }

  public hasMoreCabinet(idProfil: string): boolean {
    const total = this.totalsCabinet.get(idProfil) || 0;
    return this.getModelesCabinet(idProfil).length < total;
  }

  public async loadMoreCabinet(idProfil: string, event?: MouseEvent): Promise<void> {
    if (event) event.stopPropagation();

    if (
      this.profilsLoading.has(idProfil) ||
      this.profilsLoadingMore.has(idProfil) ||
      !this.hasMoreCabinet(idProfil)
    ) {
      return;
    }

    this.profilsLoadingMore.add(idProfil);

    try {
      const currentPage = this.pagesCabinet.get(idProfil) || 1;
      const nextPage = currentPage + 1;

      const result = await this._modeleOrdonnancePanel.findByProfil(
        idProfil,
        nextPage,
        this.cabinetPerPage
      );

      if (result) {
        const current = this.modelesCabinet.get(idProfil) || [];
        const ids = new Set(current.map(modele => modele.id));
        const nouveaux = (result.objects || []).filter(modele => !ids.has(modele.id));

        this.modelesCabinet.set(idProfil, [...current, ...nouveaux]);
        this.pagesCabinet.set(idProfil, nextPage);
        this.totalsCabinet.set(idProfil, result.total);

        const profil = this.profilsCabinet.find(p => p.idProfil === idProfil);
        if (profil) profil.total = result.total;
      }
    } finally {
      this.profilsLoadingMore.delete(idProfil);
    }
  }

  public get hasMorePerso(): boolean {
    return this.modelesPerso.length < this.persoTotal;
  }

  public async loadMorePerso(): Promise<void> {
    if (this.isLoadingMorePerso || !this.hasMorePerso) {
      return;
    }

    this.isLoadingMorePerso = true;

    try {
      const nextPage = this.persoCurrentPage + 1;

      const result = await this._modeleOrdonnancePanel.find(
        nextPage,
        this.persoPerPage,
        this.searchPerso || undefined
      );

      if (result) {
        const ids = new Set(this.modelesPerso.map(modele => modele.id));
        const nouveaux = (result.objects || []).filter(
          modele => !ids.has(modele.id)
        );

        this.modelesPerso = [...this.modelesPerso, ...nouveaux];
        this.persoCurrentPage = nextPage;
        this.persoTotal = result.total;
      }
    } finally {
      this.isLoadingMorePerso = false;
    }
  }
}
